/*
  The PCG offices a visitor can be sent to, and which of them is nearest
  (docs/specs/pop-language-split.md D20-D23, Robert, 2026-10-20).

  Two places use this: the footer's "Your nearest PCG" (D22) and the header's "Submit cards"
  (D23). Both used to send everyone to premiercardgrading.com, the US shop, so a visitor in
  Auckland or Manila landed on a storefront that does not ship to them.

  "Nearest" is the browser's time zone and nothing else (D20): no IP lookup, no outside
  service, nothing leaves the page. A zone is a city, not a country, so the map below is a
  list of IANA zone names per region (D21). A zone it does not know — and a browser that
  will not say — suggests nothing, and the visitor picks from the full list instead.

  Everything here is pure and unit-tested. The storage helpers are the one exception, and
  they are wrapped so a private window or blocked site data reads as "nothing saved" rather
  than throwing, the same as the language bucket's (lib/setLanguage).
*/

export interface Office {
  href: string;
  text: string;
}

/*
  Every PCG storefront that is open, alphabetically.

  David's review: the footer listed US, AU and NZ only. PCG has more, and a visitor on
  premiercardgrading.co.uk had no way from here back to their own shop. The eight below
  were each fetched before being listed — they return 200 and serve a real storefront.

  Two regions PCG runs are deliberately NOT here, and this is the reason, so nobody adds
  them back from a list of offices:

    - premiercardgrading.eu answers, but with Shopify's password page: "Continental
      Europe - Opening Soon". A public footer link to a locked shop is a broken promise.
    - premiercardgrading.vn does not resolve at all.

  Add either the moment it opens. The office registry is not the source of truth for this
  list — an office exists before its storefront does.

  Moved here from the footer (D21 says "only the 8 open sites, the footer's GRADING_SITES")
  so the footer's list, its office picker and Submit cards all read one list.
*/
export const GRADING_SITES: readonly Office[] = [
  { href: "https://premiercardgrading.com.au", text: "Australia" },
  { href: "https://premiercardgrading.de", text: "Germany" },
  { href: "https://premiercardgrading.jp", text: "Japan" },
  { href: "https://premiercardgrading.com.my", text: "Malaysia" },
  { href: "https://premiercardgrading.co.nz", text: "New Zealand" },
  { href: "https://premiercardgrading.ph", text: "Philippines" },
  { href: "https://premiercardgrading.co.uk", text: "United Kingdom" },
  { href: "https://premiercardgrading.com", text: "United States" },
];

/* Where Submit cards went before D23, and still goes with nothing saved or suggested. */
export const DEFAULT_SUBMIT_HREF = "https://premiercardgrading.com";

export const OFFICE_STORAGE_KEY = "pcg.pop.office";

const site = (text: string): Office => {
  const found = GRADING_SITES.find(office => office.text === text);
  if (!found) {
    throw new Error(`No open PCG site called ${text}`);
  }
  return found;
};

const AUSTRALIA = site("Australia");
const GERMANY = site("Germany");
const JAPAN = site("Japan");
const MALAYSIA = site("Malaysia");
const NEW_ZEALAND = site("New Zealand");
const PHILIPPINES = site("Philippines");
const UNITED_KINGDOM = site("United Kingdom");
const UNITED_STATES = site("United States");

/*
  D21's regions as IANA zones. Browsers report the canonical name, but a few still hand
  back an older alias (Asia/Saigon, Asia/Rangoon, US/Eastern), so the well-known aliases are
  listed beside the names they stand for.
*/
const NZ_ZONES = new Set(["Pacific/Auckland", "Pacific/Chatham", "NZ", "NZ-CHAT"]);

/* The 50 states. Puerto Rico, Guam and the other territories are not in D21's "US". */
const US_ZONES = new Set([
  "America/New_York",
  "America/Detroit",
  "America/Louisville",
  "America/Chicago",
  "America/Menominee",
  "America/Denver",
  "America/Boise",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Juneau",
  "America/Sitka",
  "America/Metlakatla",
  "America/Yakutat",
  "America/Nome",
  "America/Adak",
  "America/Fort_Wayne",
  "America/Indianapolis",
  "America/Knox_IN",
  "Pacific/Honolulu",
  "Navajo",
]);
const US_PREFIXES = ["America/Indiana/", "America/Kentucky/", "America/North_Dakota/", "US/"];

/* Canada goes to the US shop until premiercardgrading.ca opens (D21). */
const CANADA_ZONES = new Set([
  "America/St_Johns",
  "America/Halifax",
  "America/Glace_Bay",
  "America/Moncton",
  "America/Goose_Bay",
  "America/Blanc-Sablon",
  "America/Toronto",
  "America/Montreal",
  "America/Nipigon",
  "America/Thunder_Bay",
  "America/Iqaluit",
  "America/Pangnirtung",
  "America/Atikokan",
  "America/Winnipeg",
  "America/Rainy_River",
  "America/Resolute",
  "America/Rankin_Inlet",
  "America/Regina",
  "America/Swift_Current",
  "America/Edmonton",
  "America/Cambridge_Bay",
  "America/Yellowknife",
  "America/Inuvik",
  "America/Creston",
  "America/Dawson_Creek",
  "America/Fort_Nelson",
  "America/Whitehorse",
  "America/Dawson",
  "America/Vancouver",
]);
const CANADA_PREFIXES = ["Canada/"];

/* The UK, Ireland and the Crown dependencies. Checked before the rest of Europe. */
const UK_ZONES = new Set([
  "Europe/London",
  "Europe/Dublin",
  "Europe/Belfast",
  "Europe/Isle_of_Man",
  "Europe/Jersey",
  "Europe/Guernsey",
  "GB",
  "GB-Eire",
  "Eire",
]);

/*
  Germany, Austria and Switzerland get "Germany, then UK"; the rest of Europe gets
  "Germany and UK" (D21). Both come out as the same two offices in the same order, so one
  rule serves both; the DACH zones are listed so the intent survives if they ever differ.
*/
const DACH_ZONES = new Set(["Europe/Berlin", "Europe/Busingen", "Europe/Vienna", "Europe/Zurich"]);
const EUROPE_ATLANTIC_ZONES = new Set([
  "Atlantic/Canary",
  "Atlantic/Madeira",
  "Atlantic/Azores",
  "Atlantic/Reykjavik",
  "Atlantic/Faroe",
  "Atlantic/Faeroe",
]);

const JAPAN_ZONES = new Set(["Asia/Tokyo", "Japan"]);
const PHILIPPINES_ZONES = new Set(["Asia/Manila"]);

/* The rest of South-East Asia: Malaysia first, as the region's own office (D21). */
const SE_ASIA_ZONES = new Set([
  "Asia/Kuala_Lumpur",
  "Asia/Kuching",
  "Asia/Singapore",
  "Singapore",
  "Asia/Jakarta",
  "Asia/Makassar",
  "Asia/Ujung_Pandang",
  "Asia/Jayapura",
  "Asia/Pontianak",
  "Asia/Bangkok",
  "Asia/Ho_Chi_Minh",
  "Asia/Saigon",
  "Asia/Brunei",
  "Asia/Phnom_Penh",
  "Asia/Vientiane",
  "Asia/Yangon",
  "Asia/Rangoon",
  "Asia/Dili",
]);

const startsWithAny = (zone: string, prefixes: readonly string[]) =>
  prefixes.some(prefix => zone.startsWith(prefix));

/*
  D21. The offices to suggest for a time zone, nearest first; empty when the zone is
  unknown, outside every region PCG serves, or missing. A fresh array every call.
*/
export const suggestOffices = (timeZone: string | undefined): Office[] => {
  if (!timeZone) {
    return [];
  }
  if (NZ_ZONES.has(timeZone)) {
    return [NEW_ZEALAND];
  }
  if (timeZone.startsWith("Australia/")) {
    return [AUSTRALIA];
  }
  if (
    US_ZONES.has(timeZone) ||
    startsWithAny(timeZone, US_PREFIXES) ||
    CANADA_ZONES.has(timeZone) ||
    startsWithAny(timeZone, CANADA_PREFIXES)
  ) {
    return [UNITED_STATES];
  }
  if (UK_ZONES.has(timeZone)) {
    return [UNITED_KINGDOM];
  }
  if (
    DACH_ZONES.has(timeZone) ||
    timeZone.startsWith("Europe/") ||
    EUROPE_ATLANTIC_ZONES.has(timeZone)
  ) {
    return [GERMANY, UNITED_KINGDOM];
  }
  if (JAPAN_ZONES.has(timeZone)) {
    return [JAPAN];
  }
  if (PHILIPPINES_ZONES.has(timeZone)) {
    return [PHILIPPINES];
  }
  if (SE_ASIA_ZONES.has(timeZone)) {
    return [MALAYSIA, PHILIPPINES];
  }
  return [];
};

/* D20: the browser's own zone, or undefined from a browser that refuses to answer. */
export const detectTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch (e) {
    return undefined;
  }
};

/* The saved value is free text from outside; only an open site's href passes. */
export const officeByHref = (href: unknown): Office | null =>
  GRADING_SITES.find(office => office.href === href) ?? null;

export const readSavedOffice = (): Office | null => {
  try {
    return officeByHref(window.localStorage.getItem(OFFICE_STORAGE_KEY));
  } catch (e) {
    return null;
  }
};

/* Called from the visitor's own choice in the footer and from nowhere else (D22). */
export const saveOffice = (office: Office): void => {
  try {
    window.localStorage.setItem(OFFICE_STORAGE_KEY, office.href);
  } catch (e) {
    // Storage unavailable: the visitor is on their way to the office's site regardless.
  }
};

/* D23: the remembered office, else the first suggestion, else premiercardgrading.com. */
export const submitHref = (saved: Office | null, suggested: readonly Office[]): string =>
  saved?.href ?? suggested[0]?.href ?? DEFAULT_SUBMIT_HREF;

export type Social = "facebook" | "instagram" | "tiktok";

/* The order the footer shows an office's accounts in, as the storefront does. */
export const SOCIAL_ORDER: readonly Social[] = ["facebook", "instagram", "tiktok"];

export interface OfficeSocials {
  name: string;
  links: Partial<Record<Social, string>>;
}

/*
  Each office's social accounts, copied from the storefront footer's `office` blocks
  (pcg-new-theme, events-new-branch, sections/footer-group.json, 2026-10-20), in its order.
  An account an office does not have is simply left out, as the storefront does.

  Moved here from the footer (2026-10-21) so officeLinks below can join it to GRADING_SITES
  and be tested. The names are the storefront's, not GRADING_SITES': "USA", "UK".
*/
export const OFFICE_SOCIALS: readonly OfficeSocials[] = [
  {
    name: "New Zealand",
    links: {
      facebook: "https://www.facebook.com/PCGNZ1",
      instagram: "https://www.instagram.com/premiercardgrading_nz",
    },
  },
  {
    name: "Australia",
    links: {
      facebook: "https://www.facebook.com/premiercardgrading.au",
      instagram: "https://www.instagram.com/premiercardgrading_au",
      tiktok: "https://www.tiktok.com/@premiercardgrading_au",
    },
  },
  {
    name: "USA",
    links: {
      facebook: "https://www.facebook.com/premiercardgrading.us",
      instagram: "https://www.instagram.com/premiercardgrading_us",
    },
  },
  {
    name: "UK",
    links: {
      facebook: "https://www.facebook.com/premiercardgrading.uk",
      instagram: "https://www.instagram.com/premiercardgrading_uk",
    },
  },
  { name: "Europe", links: { facebook: "https://www.facebook.com/PCGEUROPE" } },
  {
    name: "SE Asia",
    links: {
      facebook: "https://www.facebook.com/PCGSEA",
      instagram: "https://www.instagram.com/premiercardgrading_sea",
    },
  },
  { name: "Japan", links: { facebook: "https://www.facebook.com/profile.php?id=100080942291438" } },
  { name: "Philippines", links: { facebook: "https://www.facebook.com/profile.php?id=61577401737898" } },
];

/*
  The storefront's short names for the two sites whose GRADING_SITES name is longer. Every
  other socials name is a site's name exactly, or no site's at all.

  Europe and SE Asia are deliberately NOT mapped. Europe's page covers the whole continent
  and premiercardgrading.eu is a locked shop, not Germany's; SE Asia's covers the region,
  not Malaysia or the Philippines alone. Pointing either at one country's site would say
  something the accounts do not, so they stay socials-only until Robert says otherwise.
*/
const SOCIALS_SITE_NAME: Readonly<Record<string, string>> = {
  USA: "United States",
  UK: "United Kingdom",
};

export interface OfficeLink {
  name: string;
  /* The office's grading site; absent for an account with no site of its own. */
  site?: Office;
  /* Its accounts, in SOCIAL_ORDER; empty for a site with none. */
  socials: { network: Social; href: string }[];
}

/*
  One row per office for the footer's "Visit or follow our offices" (Robert, 2026-10-21:
  "combine these two. So instead of having double up. Have the office which can be
  clickable to take you to the site. and then have the social media icons close").

  Every site, under its GRADING_SITES name and object, with the accounts whose name matches
  it; then every account that matched no site, under its own name. A-Z by name. Nothing is
  dropped and no URL is made up: a site with no accounts has no icons, an account with no
  site has no link.
*/
export const officeLinks = (
  sites: readonly Office[] = GRADING_SITES,
  socials: readonly OfficeSocials[] = OFFICE_SOCIALS
): OfficeLink[] => {
  const ordered = (entry: OfficeSocials | undefined) =>
    SOCIAL_ORDER.flatMap(network => {
      const href = entry?.links[network];
      return href ? [{ network, href }] : [];
    });
  const siteName = (entry: OfficeSocials) => SOCIALS_SITE_NAME[entry.name] ?? entry.name;

  const withSites: OfficeLink[] = sites.map(site => ({
    name: site.text,
    site,
    socials: ordered(socials.find(entry => siteName(entry) === site.text)),
  }));
  const withoutSites: OfficeLink[] = socials
    .filter(entry => !sites.some(site => site.text === siteName(entry)))
    .map(entry => ({ name: entry.name, socials: ordered(entry) }));

  return [...withSites, ...withoutSites].sort((a, b) => a.name.localeCompare(b.name));
};
